"use client";
import { builder, Builder, withChildren } from "@builder.io/react";
import ArticleCard from "./components/Article-Card";
import Button from "./components/Button";
import FooterLink from "./components/FooterLink";
import Input from "./components/Input";
import MultiSelectDropdown from "./components/MultiSelectDropdown";
import Nav from "./components/Nav";
import { Progress } from "./components/ui/progress";
import RadioButtonControl from "./components/RadioButtonControl";
import SwitchTab from "./components/SwitchTab";
import Tag from "./components/Tag";
import Icon from "./components/Icon";
import Pricing from "./components/Pricing";
import TeamCard from "@/components/TeamCard";
import Stepper from "@/components/Stepper";
import HtmlBlock from "@/components/HtmlBlock";
import TableOfContents, { ToCAnchor } from "@/components/TableOfContents";

builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY!);

Builder.registerComponent(FooterLink, {
  name: "FooterLink",
  inputs: [
    {
      name: "destination",
      type: "string",
      required: true,
    },
    {
      name: "text",
      type: "string",
      required: true,
    },
  ],
});

Builder.registerComponent(Button, {
  name: "Button",
  inputs: [
    {
      name: "label",
      type: "string",
      required: true,
    },
    {
      name: "size",
      type: "string",
      enum: ["Base", "Small"],
      required: true,
    },
    {
      name: "style",
      type: "string",
      enum: ["Primary", "Secondary", "White"],
      required: true,
    },
  ],
});

Builder.registerComponent(Input, {
  name: "Input",
  inputs: [
    {
      name: "buttonLabel",
      type: "string",
      required: true,
    },
    {
      name: "placeholder",
      type: "string",
      required: true,
    },
  ],
});

Builder.registerComponent(Nav, {
  name: "Nav",
  inputs: [
    {
      name: "variant",
      type: "string",
      enum: ["default", "variant2"],
    },
  ],
});

Builder.registerComponent(Tag, {
  name: "Tag",
  inputs: [
    {
      name: "label",
      type: "string",
      required: true,
    },
  ],
});

Builder.registerComponent(withChildren(Progress), {
  name: "Progress",
  inputs: [
    {
      name: "children",
      type: "string",
      hideFromUI: true,
      meta: {
        ts: "ReactNode",
      },
    },
    {
      name: "max",
      type: "number",
    },
    {
      name: "value",
      type: "number",
    },
  ],
});

Builder.registerComponent(ArticleCard, {
  name: "ArticleCard",
  inputs: [
    {
      name: "date",
      type: "string",
      required: true,
    },
    {
      name: "intro",
      type: "string",
      required: true,
    },
    {
      name: "readingTime",
      type: "string",
      required: true,
    },
    {
      name: "tagLabel",
      type: "string",
      required: true,
    },
    {
      name: "thumbnail",
      type: "string",
    },
    {
      name: "title",
      type: "string",
      required: true,
    },
  ],
});

Builder.registerComponent(TeamCard, {
  name: "TeamCard",
  inputs: [
    {
      name: "fullName",
      type: "string",
      required: true,
    },
    {
      name: "imageSrc",
      type: "string",
      required: true,
    },
    {
      name: "investsIn",
      type: "string",
      required: true,
    },
    {
      name: "bio",
      type: "string",
      required: true,
    },
    {
      name: "firmWebsite",
      type: "string",
      required: true,
    },
    {
      name: "thesis",
      type: "string",
      required: true,
    },
    {
      name: "averageCheckSize",
      type: "string",
      required: true,
    },
    {
      name: "valueProp",
      type: "string",
      required: true,
    },
    {
      name: "investorDirectoryUrl",
      type: "string",
      required: true,
    },
    {
      name: "numberOfInvestments",
      type: "string",
      required: true,
    },
    {
      name: "role",
      type: "string",
      required: true,
    },
  ],
});

Builder.registerComponent(MultiSelectDropdown, {
  name: "MultiSelectDropdown",
  inputs: [
    {
      name: "options",
      type: "list",
      subFields: [
        {
          name: "value",
          type: "string",
          defaultValue: "",
        },
        {
          name: "name",
          type: "string",
          defaultValue: "Option",
        },
      ],
      required: true,
    },
    {
      name: "name",
      type: "string",
      required: true,
    },
    {
      name: "required",
      type: "boolean",
      defaultValue: false,
    },
    {
      name: "className",
      type: "string",
      defaultValue: "",
    },
  ],
});

Builder.registerComponent(RadioButtonControl, {
  name: "RadioButtonControl",
  inputs: [
    {
      name: "className",
      type: "string",
    },
    {
      name: "name",
      type: "string",
      required: true,
    },
    {
      name: "options",
      type: "list",
      subFields: [
        {
          name: "value",
          type: "string",
          defaultValue: "",
        },
        {
          name: "name",
          type: "string",
          defaultValue: "Option",
        },
      ],
      required: true,
    },
    {
      name: "required",
      type: "boolean",
    },
    {
      name: "value",
      type: "string",
    },
  ],
});

Builder.registerComponent(SwitchTab, {
  name: "SwitchTab",
  inputs: [
    {
      name: "className",
      type: "string",
    },
    {
      name: "defaultValue",
      type: "string",
      required: true,
    },
    {
      name: "options",
      type: "list",
      subFields: [
        {
          name: "name",
          type: "string",
          defaultValue: "Tab Name",
        },
        {
          name: "description",
          type: "string",
          defaultValue: "Description of the Tab",
        },
      ],
      required: true,
    },
  ],
});

Builder.registerComponent(Icon, {
  name: "Icon",
  inputs: [
    {
      name: "name",
      type: "string",
      required: true,
      enum: [
        "add",
        "alert",
        "audio",
        "back",
        "bag",
        "bars-progress",
        "bookmark",
        "building",
        "cancel",
        "caret-down",
        "caret-up",
        "cart",
        "chart-area",
        "chart-bar",
        "chart-column",
        "chart-line",
        "chart-pie",
        "chat",
        "check",
        "checkbox",
        "checkbox-checked",
        "chevron-down",
        "chevron-up",
        "circle-check",
        "circle-x",
        "clock",
        "close",
        "coins",
        "credit-card",
        "delete",
        "dollar",
        "down",
        "download",
        "edit",
        "email",
        "error",
        "euro",
        "excel",
        "exclamation",
        "external",
        "file",
        "flag",
        "forward",
        "heart",
        "hide-password",
        "home",
        "image",
        "info",
        "info-circle",
        "key",
        "link",
        "list-check",
        "loading",
        "lock",
        "login",
        "logout",
        "menu",
        "message",
        "mobile",
        "money-bill",
        "money-trend",
        "notification",
        "pause",
        "pdf",
        "percent",
        "phone",
        "play",
        "powerpoint",
        "profile",
        "question",
        "radio",
        "radio-checked",
        "refresh",
        "save",
        "search",
        "settings",
        "show-password",
        "sort",
        "sort-down",
        "sort-up",
        "star",
        "stop",
        "success",
        "tag",
        "tags",
        "unlock",
        "up",
        "upload",
        "user",
        "users",
        "video",
        "wallet",
        "warning",
        "word",
        "x",
        "zip",
      ],
    },
    {
      name: "size",
      type: "string",
      enum: [
        "2xs",
        "xs",
        "sm",
        "lg",
        "xl",
        "2xl",
        "1x",
        "2x",
        "3x",
        "4x",
        "5x",
        "6x",
        "7x",
        "8x",
        "9x",
        "10x",
      ],
      defaultValue: "lg",
    },
  ],
});

Builder.registerComponent(Pricing, {
  name: "Pricing",
});

// Register Stepper
Builder.registerComponent(Stepper, {
  name: "Stepper",
  inputs: [
    {
      name: "segments",
      type: "number",
      required: true,
      defaultValue: 5,
      min: 1,
      helperText: "Number of bars/segments to display",
    },
    {
      name: "fillTime",
      type: "number",
      required: true,
      defaultValue: 2000,
      min: 100,
      helperText: "Time in milliseconds for each bar to fill",
    },
    {
      // This is optional—allows you to set an action in Builder that
      // triggers whenever a bar starts animating
      name: "stateVariable",
      type: "string",
      required: true,
      helperText:
        "Name of a variable to be set on 'state' with the current index segment.",
    },
  ],
});

Builder.registerComponent(HtmlBlock, {
  name: "HtmlBlock",
  inputs: [
    {
      name: "html",
      type: "richText",
      defaultValue: "",
      required: true,
      helperText: "Html content to render in the block",
    },
  ],
});

Builder.registerComponent(TableOfContents, {
  name: "TableOfContents",
  inputs: [
    {
      name: "headings",
      type: "list",
      defaultValue: [],
      subFields: [
        {
          name: "title",
          type: "string",
          defaultValue: "",
        },
        {
          name: "slug",
          type: "string",
          defaultValue: "",
        },
      ],
      required: true,
    },
  ],
});

Builder.registerComponent(ToCAnchor, {
  name: "ToCAnchor",
  inputs: [
    {
      name: "id",
      type: "string",
      required: false,
    },
    {
      name: "top",
      type: "number",
      required: true,
      defaultValue: 250,
    },
  ],
});
